"use client";

/**
 * System Card's price map: the edition's price table with sortable column
 * headers. Each header cycles ascending, descending, then the authored order.
 * Sorting rules live in `src/lib/learn/systemCardTable.ts`.
 */
import { useMemo, useState } from "react";

import type { DataTable } from "@/constants/systemCard.types";
import { trackLearnSystemCardSort } from "@/lib/analytics/client";
import { nextSortState, sortTableRows, type SortState } from "@/lib/learn/systemCardTable";

import { TrustedInlineHtml } from "./TrustedInlineHtml";

export interface PriceMapProps {
  table: DataTable;
  /** Edition slug, sent with the sort analytics event. */
  slug: string;
}

const ARIA_SORT = { asc: "ascending", desc: "descending" } as const;
const ARROW = { asc: "↑", desc: "↓" } as const;

export function PriceMap({ table, slug }: PriceMapProps) {
  const [sort, setSort] = useState<SortState>(null);
  const rows = useMemo(
    () => (sort ? sortTableRows(table.rows, sort.column, sort.direction) : table.rows),
    [table.rows, sort],
  );

  const onSort = (column: number) => {
    const next = nextSortState(sort, column);
    setSort(next);
    trackLearnSystemCardSort({ slug, column: table.columns[column], direction: next?.direction ?? "original" });
  };

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200" data-testid="price-map">
      <table className="w-full min-w-[640px] border-collapse text-left text-[14px] leading-[1.45] text-landing-dark">
        <thead className="bg-landing-bg-light">
          <tr>
            {table.columns.map((column, c) => {
              const active = sort?.column === c ? sort.direction : null;
              return (
                <th
                  key={column}
                  scope="col"
                  aria-sort={active ? ARIA_SORT[active] : undefined}
                  className="whitespace-nowrap border-b border-slate-200 px-3 py-2 text-[12px] font-semibold uppercase tracking-[0.06em] text-landing-secondary"
                >
                  <button
                    type="button"
                    onClick={() => onSort(c)}
                    className={`inline-flex items-center gap-1 rounded uppercase tracking-[inherit] hover:text-lev-orange focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lev-orange/40 ${active ? "text-lev-orange" : ""}`}
                  >
                    {column}
                    <span aria-hidden="true" className="w-3">
                      {active ? ARROW[active] : ""}
                    </span>
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            // Sorting returns the same row objects, so the authored index is a stable key.
            <tr key={table.rows.indexOf(row)} className="border-b border-slate-100 last:border-b-0">
              {row.map((cell, c) => (
                <td
                  key={table.columns[c]}
                  className={`px-3 py-2 align-top ${typeof cell.sortValue === "number" ? "tabular-nums" : ""}`}
                >
                  <TrustedInlineHtml html={cell.html} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
